import { z } from "zod";
import { vkPlayConfigSchema } from "@frontier/protocol/auth-contracts";

type LoginStatus = 0 | 1 | 2 | 3;

export interface VkPlaySdk {
  loginStatus(): Promise<LoginStatus>;
  continueLogin(): void;
  reload(): void;
}

export interface VkPlayCallbackResponse {
  status?: string;
  loginStatus?: number | string;
  uid?: number | string;
  hash?: string;
  errcode?: number | string;
  errmsg?: string;
}

export interface VkPlayCallbacks {
  appid: number;
  getLoginStatusCallback(response?: VkPlayCallbackResponse | null): void;
  registerUserCallback(response?: VkPlayCallbackResponse | null): void;
  confirmWindowClosedCallback(): void;
}

export interface VkPlayExternalApi {
  getLoginStatus(): void;
  authUser(): void;
  registerUser(): void;
  reloadWindow(): void;
}

export type VkPlayIframeApi = (callbacks: VkPlayCallbacks) => Promise<VkPlayExternalApi>;

declare global {
  interface Window {
    iframeApi?: VkPlayIframeApi;
  }
}

const SDK_TIMEOUT = 15000;
const STATUS_TIMEOUT = 10000;
const initializations = new Map<string, Promise<VkPlaySdk>>();
const scriptLoads = new Map<string, Promise<VkPlayIframeApi>>();
const statusValueSchema = z.union([
  z.literal(0), z.literal(1), z.literal(2), z.literal(3),
  z.literal("0"), z.literal("1"), z.literal("2"), z.literal("3"),
]);
const statusResponseSchema = z.union([
  z.object({ status: z.literal("ok"), loginStatus: statusValueSchema }).strict(),
  z.object({ status: z.literal("error"), errcode: z.union([z.number().int(), z.string()]), errmsg: z.string() }).strict(),
]);
const registrationResponseSchema = z.object({
  status: z.literal("ok"), uid: z.union([z.number().int(), z.string()]).optional(), hash: z.string().optional(),
}).strict();

interface StatusRequest {
  promise: Promise<LoginStatus>;
  resolve: (status: LoginStatus) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

function loadFactory(appId: string): Promise<VkPlayIframeApi> {
  if (typeof window.iframeApi === "function") return Promise.resolve(window.iframeApi);
  const existing = scriptLoads.get(appId);
  if (existing) return existing;
  const loading = new Promise<VkPlayIframeApi>((resolve, reject) => {
    const script = document.createElement("script");
    let settled = false;
    const finish = (factory?: VkPlayIframeApi) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      if (factory) resolve(factory);
      else {
        script.remove();
        reject(new Error("Не удалось подключиться к VK Play. Повтори подключение."));
      }
    };
    const timer = setTimeout(() => finish(), SDK_TIMEOUT);
    script.src = `https://vkplay.ru/app/${appId}/static/mailru.core.js`;
    script.async = true;
    script.referrerPolicy = "no-referrer";
    script.onload = () => finish(typeof window.iframeApi === "function" ? window.iframeApi : undefined);
    script.onerror = () => finish();
    document.head.append(script);
  });
  scriptLoads.set(appId, loading);
  void loading.catch(() => {
    if (scriptLoads.get(appId) === loading) scriptLoads.delete(appId);
  });
  return loading;
}

function handshake(factory: VkPlayIframeApi, callbacks: VkPlayCallbacks): Promise<VkPlayExternalApi> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("VK Play не отвечает. Повтори подключение.")), SDK_TIMEOUT);
    try {
      factory(callbacks).then(api => { clearTimeout(timer); resolve(api); }, () => {
        clearTimeout(timer);
        reject(new Error("Не удалось подключиться к VK Play. Повторите попытку."));
      });
    } catch {
      clearTimeout(timer);
      reject(new Error("Не удалось подключиться к VK Play. Повторите попытку."));
    }
  });
}

async function createSdk(appId: string): Promise<VkPlaySdk> {
  const factory = await loadFactory(appId);
  let api: VkPlayExternalApi | null = null;
  let lastStatus: LoginStatus | null = null;
  let pending: StatusRequest | null = null;
  let registrationRequested = false;
  const failStatus = (message: string) => {
    const request = pending;
    if (!request) return;
    pending = null;
    lastStatus = null;
    clearTimeout(request.timer);
    request.reject(new Error(message));
  };
  const callbacks: VkPlayCallbacks = {
    appid: Number(appId),
    getLoginStatusCallback(response) {
      if (!pending) return;
      const result = statusResponseSchema.safeParse(response);
      if (!result.success || result.data.status !== "ok") {
        failStatus("Не удалось проверить вход в VK Play. Повторите попытку.");
        return;
      }
      const value = result.data.loginStatus;
      const status = value === 0 || value === "0" ? 0 : value === 1 || value === "1" ? 1 : value === 2 || value === "2" ? 2 : 3;
      const request = pending;
      pending = null;
      lastStatus = status;
      clearTimeout(request.timer);
      request.resolve(status);
    },
    registerUserCallback(response) {
      const requested = registrationRequested;
      registrationRequested = false;
      if (requested && registrationResponseSchema.safeParse(response).success) api?.reloadWindow();
    },
    confirmWindowClosedCallback() { registrationRequested = false; },
  };
  api = await handshake(factory, callbacks);
  const connected = api;
  return {
    loginStatus() {
      if (pending) return pending.promise;
      let resolveStatus: (status: LoginStatus) => void = () => {};
      let rejectStatus: (error: Error) => void = () => {};
      const promise = new Promise<LoginStatus>((resolve, reject) => {
        resolveStatus = resolve;
        rejectStatus = reject;
      });
      pending = {
        promise, resolve: resolveStatus, reject: rejectStatus,
        timer: setTimeout(() => failStatus("VK Play не отвечает. Повтори подключение."), STATUS_TIMEOUT),
      };
      try { connected.getLoginStatus(); }
      catch { failStatus("Не удалось проверить вход в VK Play. Повторите попытку."); }
      return promise;
    },
    continueLogin() {
      if (lastStatus === null) throw new Error("Сначала проверьте состояние входа в VK Play.");
      if (registrationRequested) return;
      if (lastStatus === 0) connected.authUser();
      else if (lastStatus === 1) {
        registrationRequested = true;
        try { connected.registerUser(); }
        catch {
          registrationRequested = false;
          throw new Error("Не удалось открыть окно согласия VK Play. Повторите попытку.");
        }
      }
      else connected.reloadWindow();
    },
    reload() { connected.reloadWindow(); },
  };
}

/** SDK state drives platform UI only; signed browser launch parameters prove identity on the server. */
export function initializeVkPlaySdk(appId: string): Promise<VkPlaySdk> {
  if (!vkPlayConfigSchema.safeParse({ enabled: true, appId }).success)
    return Promise.reject(new Error("Некорректный идентификатор приложения VK Play."));
  const existing = initializations.get(appId);
  if (existing) return existing;
  const initializing = createSdk(appId);
  initializations.set(appId, initializing);
  void initializing.catch(() => {
    if (initializations.get(appId) === initializing) initializations.delete(appId);
  });
  return initializing;
}
